import React, { useEffect, useState } from 'react'
import {
	Grid,
	Typography,
	TextField,
	MenuItem,
	Chip,
	Button,
	FormControl,
	Container,
	Tooltip,
	Box
} from '@material-ui/core'
import WizardFooter from 'components/Wizard/WizardFooter'
import { useOnboardingStore } from 'screens/Onboarding/hooks/useOnboardingStore'
import { ContentWrapper } from 'screens/Onboarding/components/ContentWrapper'
import { DeleteOutline, EditOutlined, KeyboardReturn } from '@material-ui/icons'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import axios from 'axios'
import { useStoreon } from 'storeon/react'
import { useEntryFormStore } from '../../hooks/useEntryFormStore'
import CreationContent from 'components/CreationContent'
import useIntl from 'hooks/useIntl'
// import { CardsContainer } from './EntryFormChallengePreview.style'

interface SecondComponentProps {
	maxQuestions: number
}
interface SubSection {
	subSectionId: string
	subSectionName: string
	count: number
}

interface Section {
	sectionId: string
	sectionName: string
	count: number
	type?: string
	subSections?: SubSection[]
}
const useStyles = makeStyles(theme => ({
	rowContainer: {
		display: 'flex',
		alignItems: 'center',
		padding: '12px 16px',
		borderRadius: '6px',
		// marginBottom: '10px',
		marginTop: '5px',
		justifyContent: 'space-between'
	},
	evenRow: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.paper
				: theme.palette.grey[100]
	},
	oddRow: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.default
				: theme.palette.grey[200]
	},
	textClamp: {
		maxWidth: 600,
		display: '-webkit-box',
		WebkitLineClamp: 1,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'normal'
	}
}))

const EntryFormChallengePreview: React.FC<SecondComponentProps> = ({ maxQuestions }) => {
	const { currentStep, prevStep, nextStep, toggleHelpGuide } = useOnboardingStore()

	const { formStates, updateEntryFormState } = useEntryFormStore()
	// const formStates = useEntryFormStore(state => state.formStates)
	// const updateEntryFormState = useEntryFormStore(state => state.updateEntryFormState)

	const [questions, setQuestions] = useState<string[]>([])

	const [selectedType, setSelectedType] = useState<string | null>(
		formStates.selectedType || null
	)

	const [selectedSubTypes, setSelectedSubTypes] = useState<string>(
		formStates.selectedSubTypes || ''
	)
	const [numQuestions, setNumQuestions] = useState<number | ''>(
		formStates.numQuestions || ''
	)
	const [chartData, setChartData] = useState(formStates.chartData || [])
	const [editingIndex, setEditingIndex] = useState<number | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [sections, setSections] = useState<Section[]>([])
	const [subSections, setSubSections] = useState<SubSection[]>([])
	const [questionTypes, setQuestionTypes] = useState<Section[]>([])
	const { token } = useStoreon('token')
	const classes = useStyles()
	const [selectedSubSectionName, setSelectedSubSectionName] = useState('')
	const intl = useIntl()
	const handleAddOrUpdate = () => {
		if (!numQuestions || !selectedType || Number(numQuestions) <= 0) {
			setError('Enter a valid number of questions.')
			return
		}

		const selectedQuestionType = questionTypes.find(q => q.sectionId === selectedType)
		if (!selectedQuestionType) {
			setError('Invalid question type.')
			return
		}

		const maxAllowed = selectedQuestionType.count
		const topicName = selectedQuestionType.sectionName

		const totalExistingQuestionsForType = chartData.reduce(
			(total, row, index) =>
				index !== editingIndex && row.sectionId === selectedType
					? total + row.questions
					: total,
			0
		)

		const newTotalForType = totalExistingQuestionsForType + Number(numQuestions)

		if (newTotalForType > maxAllowed) {
			setError(
				`You can add only ${
					maxAllowed - totalExistingQuestionsForType
				} more questions for this topic.`
			)
			return
		}

		const totalExistingQuestions = chartData.reduce(
			(total, row, index) => (index !== editingIndex ? total + row.questions : total),
			0
		)

		const newTotal =
			totalExistingQuestions +
			Number(numQuestions) -
			(editingIndex !== null ? chartData[editingIndex].questions : 0)

		if (newTotal > maxQuestions) {
			setError(`Cannot exceed total limit of ${maxQuestions} questions!`)
			return
		}

		const selectedSubTypeNames = selectedSubTypes
			.split(',')
			.map(subTypeId => {
				const sub = subSections.find(sub => sub.subSectionId === subTypeId.trim())
				return sub?.subSectionName || ''
			})
			.filter(name => name)

		// console.log('Selected SubType Names:', selectedSubTypeNames)

		setError('')
		const newData = {
			type: topicName,
			sectionId: selectedQuestionType.sectionId,
			subSectionId: selectedSubTypes.split(',')[0] || '',
			subTypes: selectedSubTypeNames.join(', '),

			questions: Number(numQuestions),
			count: maxAllowed
		}

		if (editingIndex !== null) {
			setChartData(prev =>
				prev.map((item, index) =>
					index === editingIndex
						? { ...item, ...newData, questions: Number(numQuestions) }
						: item
				)
			)
			setEditingIndex(null)
		} else {
			setChartData(prev => [...prev, newData])
		}
		setSubSections([])
		resetForm()
	}

	const totalQuestions = chartData.reduce((total, row) => total + row.questions, 0)

	// const handleEditRow = (index: number) => {
	// 	const { type, subSectionId, questions } = chartData[index]

	// 	const selectedQuestionType = questionTypes.find(q => q.sectionName === type)

	// 	if (selectedQuestionType) {
	// 		setSelectedType(selectedQuestionType.sectionId)
	// 		setSelectedSubTypes(subSectionId || '')
	// 		setNumQuestions(questions)
	// 		setEditingIndex(index)
	// 	}
	// }
	const handleEditRow = (index: number) => {
		const { type, subSectionId, questions } = chartData[index]

		const selectedQuestionType = questionTypes.find(q => q.sectionName === type)

		if (selectedQuestionType) {
			setSelectedType(selectedQuestionType.sectionId)
			setSelectedSubTypes(subSectionId || '')
			setNumQuestions(questions)
			setEditingIndex(index)

			fetchSubSectionsWithSectionId(selectedQuestionType.sectionId)
		}
	}
	const handleDeleteRow = (index: number) => {
		setChartData(prev => prev.filter((_, i) => i !== index))
		setEditingIndex(null)
	}

	const resetForm = () => {
		setNumQuestions('')
		setSelectedType(null)
		setSelectedSubTypes('')
		setSelectedSubSectionName('')
		setEditingIndex(null)
	}

	const handleCancel = () => {
		setSelectedType('')
		setSelectedSubTypes('')
		setNumQuestions('')
	}

	const handleBack = () => {
		updateEntryFormState({
			selectedType,
			selectedSubTypes,
			numQuestions,
			chartData
		})
		prevStep()
	}
	const handleContinue = () => {
		updateEntryFormState({
			selectedType,
			selectedSubTypes,
			numQuestions,
			chartData
		})
		nextStep()
	}

	const canContinue = chartData.length > 0

	const fetchSubSectionsWithSectionId = async (sectionId: string) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/getSubSectionsWithSectionId/${sectionId}`,
				{
					headers: { Authorization: ` ${token} ` }
				}
			)

			const formatted = response.data.map(item => ({
				subSectionId: item._id,
				subSectionName: item.name,
				sectionName: item.sectionName,
				count: item.questionCount
			}))

			setSubSections(formatted)
		} catch (error) {
			console.error('Error fetching subsections:', error)
		}
	}

	useEffect(() => {
		const fetchSections = async () => {
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_URL}/api/v1/custom/getSections`,
					{
						headers: {
							Authorization: ` ${token}`
						}
					}
				)

				const sections = response.data.map((section: any) => ({
					sectionId: section._id,
					sectionName: section.name,
					count: section.questionCount,
					subSections: section.subSections || []
				}))

				setQuestionTypes(sections)
			} catch (error) {
				console.error('Error fetching sections:', error)
			}
		}

		fetchSections()
	}, [])

	const handleTypeSelection = async (sectionId: string) => {
		setSelectedType(sectionId)
		setSelectedSubTypes('')
		setSelectedSubSectionName('')

		await fetchSubSectionsWithSectionId(sectionId)
	}

	const handleSubTypeSelection = (subSectionId: string) => {
		setSelectedSubTypes(subSectionId)
		const selected = subSections.find(sub => sub.subSectionId === subSectionId)
		if (selected) {
			setSelectedSubSectionName(selected.subSectionName)
		}
	}

	const handleNumQuestionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value ? parseInt(e.target.value, 10) : ''

		const totalExistingQuestions = chartData.reduce(
			(total, row) => total + row.questions,
			0
		)

		const newTotal = totalExistingQuestions + (typeof value === 'number' ? value : 0)

		if (newTotal > maxQuestions) {
			setError(`Cannot exceed total limit of ${maxQuestions} questions!`)
		} else if (value <= 0) {
			setError('Number of questions must be positive.')
		} else {
			setError(null)
		}

		setNumQuestions(value)
	}
	return (
		<>
			<ContentWrapper currentStep={currentStep}>
				<CreationContent
					subHeader
					title={intl.formatMessage({
						id: 'onboarding.select.entry.form.subTitle.2'
					})}
					subTitle={intl.formatMessage({
						id: 'onboarding.select.entry.form.title'
					})}
				>
					{/* <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
					Pick Questions
				</Typography> */}

					<Grid
						container
						spacing={2}
						// alignItems="center"
						sx={{
							// '@media (max-width: 600px)': {
							// 	flexDirection: 'column',
							// 	alignItems: 'stretch',
							mt: 2
							// }
						}}
					>
						<Grid item xs={6} sm={2}>
							<FormControl variant="outlined" fullWidth>
								<Typography variant="body2" gutterBottom>
									No. of Questions
								</Typography>
								<TextField
									type="number"
									placeholder="Enter a number"
									size="small"
									sx={{ height: '40px' }}
									value={numQuestions}
									onChange={handleNumQuestionsChange}
									fullWidth
									error={!!error || numQuestions === 0}
									helperText={
										error || (numQuestions === 0 ? 'Please enter a positive number' : '')
									}
									inputProps={{
										min: 1
									}}
								/>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={4}>
							<FormControl variant="outlined" fullWidth>
								<Typography variant="body2" gutterBottom>
									Select Topic
								</Typography>

								<TextField
									select
									fullWidth
									size="small"
									value={selectedType || ''}
									onChange={e => handleTypeSelection(e.target.value)}
									SelectProps={{
										MenuProps: {
											getContentAnchorEl: null,
											anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
											transformOrigin: { vertical: 'top', horizontal: 'left' },
											PaperProps: {
												style: { maxHeight: 200, overflowY: 'auto', maxWidth: 200 }
											}
										},
										displayEmpty: true
									}}
								>
									<MenuItem value="" disabled>
										Select a Topic
									</MenuItem>
									{questionTypes.map(({ sectionId, sectionName, count }) => (
										<MenuItem
											key={sectionId}
											value={sectionId}
											sx={{
												display: 'flex',
												alignItems: 'center',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap'
											}}
										>
											{/* {sectionName} &nbsp;-&nbsp; ({count}) */}
											<span
												style={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap'
												}}
											>
												{sectionName} &nbsp;-&nbsp; ({count})
											</span>
										</MenuItem>
									))}
								</TextField>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={4}>
							<FormControl variant="outlined" fullWidth>
								<Typography variant="body2" gutterBottom>
									Select Sub Topic
								</Typography>

								<TextField
									select
									fullWidth
									size="small"
									// sx={{ height: '40px' }}
									value={selectedSubTypes || ''}
									onChange={e => handleSubTypeSelection(e.target.value)}
									SelectProps={{
										MenuProps: {
											getContentAnchorEl: null,
											anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
											transformOrigin: { vertical: 'top', horizontal: 'left' },
											PaperProps: {
												style: { maxHeight: 200, overflowY: 'auto', maxWidth: 200 }
											}
										},
										displayEmpty: true
									}}
								>
									<MenuItem value="" disabled>
										Select a Sub Topic
									</MenuItem>
									{/* {subSections.map(({ subSectionId, subSectionName, count }) => (
									<MenuItem key={subSectionId} value={subSectionId}>
										{subSectionName} &nbsp;-&nbsp; ({count})
									</MenuItem>
								))} */}
									{subSections.map(({ subSectionId, subSectionName, count }) => (
										<MenuItem
											key={subSectionId}
											value={subSectionId}
											sx={{
												display: 'flex',
												alignItems: 'center',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap'
											}}
										>
											<span
												style={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap'
												}}
											>
												{subSectionName} &nbsp;-&nbsp; ({count})
											</span>
										</MenuItem>
									))}
								</TextField>
							</FormControl>
						</Grid>
						<Grid
							item
							container
							justifyContent="flex-end"
							spacing={2}
							// style={{ marginTop: '4px', marginBottom: '20px' }}
							mb={2}
							sx={{
								'@media (max-width: 600px)': {
									justifyContent: 'center'
								}
							}}
							marginRight={20}
						>
							<Grid item>
								<Button
									variant="contained"
									color="primary"
									onClick={() => {
										handleAddOrUpdate()
									}}
								>
									{editingIndex !== null ? 'Update' : 'Add'}
								</Button>
							</Grid>
							<Grid item>
								<Button variant="outlined" onClick={handleCancel}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={10}>
						<Container>
							{chartData.map((row, index) => (
								<div
									key={index}
									className={`${classes.rowContainer} ${
										index % 2 === 0 ? classes.oddRow : classes.evenRow
									}`}
								>
									<div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
										<Typography
											variant="body1"
											style={{
												textAlign: 'left',
												paddingLeft: '8px',
												marginRight: '10px'
											}}
										>
											{row.questions}
										</Typography>

										<Box
											sx={{
												display: 'inline-block',
												textOverflow: 'ellipsis',
												overflow: 'hidden',
												whiteSpace: 'nowrap',
												width: 'calc(100% - 80px)'
											}}
											title={`${row.type} ${row.subTypes ? `- ${row.subTypes}` : ''} - (${
												row.count
											})`}
										>
											<Typography
												className={classes.textClamp}
												style={{
													textAlign: 'left',
													paddingLeft: '8px',
													marginRight: '10px'
												}}
												noWrap
											>
												{row.type} {row.subTypes ? `- ${row.subTypes}` : ''} - ({row.count})
											</Typography>
										</Box>
									</div>

									<div
										style={{
											display: 'flex',
											gap: '20px',
											cursor: 'pointer',
											paddingRight: '8px'
										}}
									>
										<EditOutlined
											onClick={() => handleEditRow(index)}
											style={{ color: '#7D6EF1' }}
										/>
										<DeleteOutline
											onClick={() => handleDeleteRow(index)}
											style={{ color: 'red' }}
										/>
									</div>
								</div>
							))}
							{chartData.length > 0 && (
								<Typography
									variant="body1"
									sx={{
										fontWeight: 'bold',
										marginTop: 2
									}}
								>
									Total : {totalQuestions}
								</Typography>
							)}
							{/* </CardsContainer> */}
						</Container>
					</Grid>
				</CreationContent>
			</ContentWrapper>

			<WizardFooter
				onOpenHelpGuide={toggleHelpGuide}
				continueDisabled={!canContinue}
				onBack={handleBack}
				onContinue={handleContinue}
			/>
		</>
	)
}

export default EntryFormChallengePreview
